import InfoIcon from '@mui/icons-material/Info';
import {
  Button,
  Fade,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Theme,
  Tooltip,
  TooltipProps,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useRecoilStateLoadable, useRecoilValueLoadable } from 'recoil';
import {
  FormikSwitch,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import { trackSaveAddLeaderboardSlide } from '../../../common/userTracking';
import { IUserAtom, user } from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';
import { SlideMetric } from '../../pages/leaderboard/Slide';
import { MetricDropdownFormik } from '../dropdowns/MetricDropdownFormik';

const useStyles = makeStyles((theme: Theme) => ({
  fullWidth: {
    width: '100% !important',
  },
  actionColumn: {
    display: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  numbersList: {
    padding: '0',
    paddingBottom: '1px',
    listStyleType: 'none',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  labels: {
    marginBottom: 0,
    paddingBottom: 0,
    fontSize: '12px',
  },
  formContainer: {
    marginTop: '20px',
    marginBottom: '30px',
    width: '60%',
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  icon: {
    color: 'gray',
  },
}));

const InfoTooltip = (props: Omit<TooltipProps, 'children'>) => {
  return (
    <Tooltip
      style={{ cursor: 'pointer' }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 600 }}
      placement="right-start"
      {...props}
    >
      <InfoIcon style={{ color: 'gray' }} />
    </Tooltip>
  );
};

export const AgentSlideForm = (props: Record<any, any>) => {
  const [showMessage, setShowMessage] = useState(false);
  const [autocomplete, setAutocomplete] = useState<any>({
    lastUpdated: '',
    memberCount: 0,
    teamid: 0,
    teamId: 0,
    teamname: 'All Agents',
  });
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, any>
  >(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const history = useHistory();

  const businessTeams = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_TEAMS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_TEAMS,
      }),
    })
  );
  type Team = {
    lastUpdated: string;
    memberCount: number;
    teamId: number;
    teamid: number;
    teamname: string;
  };

  const initialValues = useMemo<any>(() => {
    const slide = props?.props?.data?.[props.props.openIndex] ?? {};
    return {
      teamDropdown: slide?.teamId ?? {
        lastUpdated: '',
        memberCount: 0,
        teamid: 0,
        teamId: 0,
        teamname: 'All Agents',
      },
      coverTime: slide?.timePeriod ?? '',
      metric: slide?.metric ?? SlideMetric.allconnections,
      slideTime: slide?.slideDurationInSec ?? '',
      showAllUsers: (slide?.showAllUsers ?? 1) === 1 || slide?.showAllUsers,
    };
  }, [
    props.props.id,
    props.props.teamsList.current,
    businessTeams,
    props.props.id,
  ]);
  useEffect(() => {
    const slide = props?.props?.data?.[props.props.openIndex] ?? {};
    const team = props.props.teamsList.current?.filter(
      (f: Team) => f.teamid === slide.teamId
    )?.[0];
    setAutocomplete(
      team ?? {
        lastUpdated: '',
        memberCount: 0,
        teamid: 0,
        teamId: 0,
        teamname: 'All Agents',
      }
    );
  }, [businessTeams, props.props.teamsList, props.props.id]);
  const classes = useStyles();
  return (
    <Formik
      enableReinitialize={true}
      onSubmit={async (values, actions) => {
        trackSaveAddLeaderboardSlide();
        const updatedSlide = props.props.data[props.props.openIndex];
        const newSlide = {
          // ...updatedSlide,
          active: updatedSlide?.active ? 1 : 0,
          type: updatedSlide.type,
          teamId:
            typeof values.teamDropdown === 'number' ? values.teamDropdown : 0,
          metric: values.metric,
          timePeriod: values.coverTime,
          slideDurationInSec: values.slideTime,
          showAllUsers: values.showAllUsers ? 1 : 0,
        };
        const upsertSlidesArr = [...props.props.data];
        upsertSlidesArr[props.props.openIndex] = newSlide;

        const payload = {
          businessid: userAtomContents?.businessid,
          leaderboardId:
            props.props.leaderboardId === 'new'
              ? null
              : props.props.leaderboardId,
          leaderboardName: props.props.newLeaderboard
            ? props.props.leaderboardName
            : props.props.leaderboardId === 'new'
            ? `Leaderboard ${props.props.numbLeaderboards + 1}`
            : props.props.leaderboardName,
          createdById: updatedSlide.createdById ?? userAtomContents.userid,
          slides: JSON.stringify(upsertSlidesArr),
        };

        const upsertRes = await mutation.mutateAsync({
          domain: process.env.VITE_APP_CDK_DOMAIN,
          endpoint: endpointEnum.UPSERT_LEADERBOARD,
          method: 'post',
          payload,
        });
        if (upsertRes?.data?.success) {
          props.props.setRefetchToggleForLeaderboards({});
          enqueueSnackbarWithStyles({
            message: `Successfully updated slide`,
            alertProps: { severity: 'success' },
          });
          if (props.props.leaderboardId === 'new') {
            const pathArr = window.location.pathname.split('/');
            pathArr[pathArr.length - 1] = upsertRes.data?.data?.leaderboardId;
            history.push(pathArr.join('/'));
          }
        } else {
          enqueueSnackbarWithStyles({
            message: `Something went wrong, slide failed to update`,
            alertProps: { severity: 'error' },
          });
        }
        props.props.resetStates();
      }}
      initialValues={initialValues}
    >
      {(formik) => {
        const over100 =
          (autocomplete?.teamId == 0 && props.props.numbUsers > 100) ||
          autocomplete?.memberCount > 100;

        const over500 =
          (autocomplete?.teamId == 0 && props.props.numbUsers > 500) ||
          autocomplete?.memberCount > 500;

        return (
          <form>
            <div className={`${classes.formContainer} container`}>
              <p className={classes.labels}>
                Select the team this slide should display
              </p>
              <div className="row">
                <div className="col-lg-8">
                  <MaterialAutocomplete
                    style={{ width: '400px' }}
                    className={`flex-grow-1 ${classes.field}`}
                    type="text"
                    label="Teams"
                    size="small"
                    name="teamDropdown"
                    variant="outlined"
                    options={props.props.teamsList.current ?? []}
                    value={autocomplete}
                    multiple={false}
                    onChange={(e, newValue: Team) => {
                      setAutocomplete(newValue);
                      formik.setFieldValue('teamDropdown', newValue?.teamid);

                      if (formik.values.showAllUsers) {
                        if (
                          ((newValue?.teamId === 0 &&
                            props.props.numbUsers > 500) ||
                            newValue?.memberCount > 500) &&
                          formik.values.slideTime !== '5minutes'
                        ) {
                          formik.setFieldValue('slideTime', '');
                        } else if (
                          ((newValue?.teamId === 0 &&
                            props.props.numbUsers > 100) ||
                            newValue?.memberCount > 100) &&
                          (formik.values.slideTime === '15seconds' ||
                            formik.values.slideTime === '30seconds')
                        ) {
                          formik.setFieldValue('slideTime', '');
                        }
                      }
                    }}
                    getOptionLabel={(option) => option.teamname ?? ''}
                    isOptionEqualToValue={(option, value) => {
                      return option?.teamid === value?.teamid;
                    }}
                    disabled={businessTeams.state === 'loading'} // in progress or queue loading
                  />
                </div>
              </div>
              <p className={classes.labels}>
                What sort of metrics would you like this slide to display?
              </p>
              <div className="row">
                <div
                  className="col-lg-8"
                  style={{ marginLeft: '0', paddingLeft: '0' }}
                >
                  <MetricDropdownFormik
                    customStyles={{ width: '400px' }}
                    handleOnChange={(e) => {
                      formik.setFieldValue('metric', e);
                    }}
                    formik={formik}
                    defaultMetric={formik.initialValues.metric}
                  />
                </div>
              </div>
              <FormControl>
                <p className={classes.labels} style={{ marginTop: '20px' }}>
                  What period of time should these metrics cover?
                </p>
                <RadioGroup
                  aria-labelledby="radio-buttons-group-cover-time"
                  name="coverTime"
                  style={{ marginLeft: '13px' }}
                  defaultValue={initialValues?.coverTime ?? ''}
                  onChange={(e) => {
                    formik.setFieldValue('coverTime', (e.target as any).value);
                  }}
                >
                  <div
                    className="row"
                    style={{ width: '175%', maxWidth: '450px' }}
                  >
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="past14hours"
                        control={<Radio color="primary" />}
                        label="Past 14 hours"
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="past24hours"
                        control={<Radio color="primary" />}
                        label="Past 24 hours"
                      />
                    </div>
                  </div>
                  <div
                    className="row"
                    style={{ width: '175%', maxWidth: '450px' }}
                  >
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="last7days"
                        control={<Radio color="primary" />}
                        label="Last 7 days"
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="last30days"
                        control={<Radio color="primary" />}
                        label="Last 30 days"
                      />
                    </div>
                  </div>
                </RadioGroup>
                <div style={{ display: 'flex' }}>
                  <p className={classes.labels} style={{ marginRight: '5px' }}>
                    How long should this slide display?
                  </p>
                  <InfoTooltip title="Slides with > 100 users will display a minimum of 1 minute and and >500 users a minimum of 5 minutes" />
                </div>
                <RadioGroup
                  aria-labelledby="radio-buttons-group-slide-time"
                  name="slideTime"
                  style={{ marginLeft: '13px' }}
                  // defaultValue={initialValues?.slideTime ?? ''}
                  value={formik.values.slideTime}
                  onChange={(e) => {
                    formik.setFieldValue('slideTime', (e.target as any).value);
                  }}
                >
                  <div
                    className="row"
                    style={{ width: '175%', maxWidth: '450px' }}
                  >
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="15seconds"
                        control={<Radio color="primary" />}
                        label="15 seconds"
                        disabled={
                          !formik.values.showAllUsers
                            ? false
                            : autocomplete?.teamId === 0
                            ? props.props.numbUsers > 100
                            : autocomplete?.memberCount > 100
                        }
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="30seconds"
                        control={<Radio color="primary" />}
                        label="30 seconds"
                        disabled={
                          !formik.values.showAllUsers
                            ? false
                            : autocomplete?.teamId === 0
                            ? props.props.numbUsers > 100
                            : autocomplete?.memberCount > 100
                        }
                      />
                    </div>
                  </div>
                  <div
                    className="row"
                    style={{ width: '175%', maxWidth: '450px' }}
                  >
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="1minute"
                        control={<Radio color="primary" />}
                        label="1 minute"
                        disabled={
                          !formik.values.showAllUsers
                            ? false
                            : autocomplete?.teamId === 0
                            ? props.props.numbUsers > 500
                            : autocomplete?.memberCount > 500
                        }
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <FormControlLabel
                        value="5minutes"
                        control={<Radio color="primary" />}
                        label="5 minutes"
                      />
                    </div>
                  </div>
                </RadioGroup>
                <div style={{ display: 'inline', marginBottom: '5px' }}>
                  <FormControlLabel
                    style={{ marginBottom: '0', marginRight: '4px' }}
                    control={
                      <FormikSwitch
                        name="showAllUsers"
                        formik={formik}
                        color="primary"
                        type="checkbox"
                        handleChange={() => {
                          if (formik.values.showAllUsers) {
                            formik.setFieldValue('showAllUsers', false);
                          } else {
                            formik.setFieldValue('showAllUsers', true);

                            if (
                              over500 &&
                              formik.values.slideTime !== '5minutes'
                            ) {
                              formik.setFieldValue('slideTime', '');
                            } else if (
                              over100 &&
                              (formik.values.slideTime === '15seconds' ||
                                formik.values.slideTime === '30seconds')
                            ) {
                              formik.setFieldValue('slideTime', '');
                            }
                          }
                        }}
                      />
                    }
                    label="Show All Users"
                  />
                  <InfoTooltip title="If 'Show All Users' is toggled off, only the top 10 users will show" />
                </div>
                {showMessage && (
                  <p style={{ whiteSpace: 'pre-line', color: 'red' }}>
                    This slide has unsaved changes, your changes will be lost
                    when you press cancel
                  </p>
                )}
                <div className="row">
                  <Button
                    variant="contained"
                    color="inherit"
                    disableElevation={true}
                    className={classes.button}
                    onClick={() => {
                      if (showMessage) {
                        props.props.resetStatesOnCancel();
                      } else {
                        if (formik.dirty) {
                          setShowMessage(true);
                        } else {
                          props.props.resetStatesOnCancel();
                        }
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation={true}
                    color="primary"
                    disabled={
                      !formik.dirty ||
                      formik.values.slideTime === '' ||
                      formik.values.coverTime === '' ||
                      autocomplete === null
                    }
                    onClick={() => {
                      formik.handleSubmit();
                    }}
                    className={classes.button}
                  >
                    Save
                  </Button>
                </div>
              </FormControl>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
